import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button, Dialog, Field, SelectMenu, SelectMenuItem } from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { flattenFolders, useFolderTree } from '../folders/api';
import { useMoveTestCases } from './api';

export interface MoveCaseDialogProps {
  projectCode: string;
  /** The case being moved, or `null` when the dialog is closed. */
  testCase: { id: string; caseKey: string; folderId: string } | null;
  onClose: () => void;
}

/** Moves one test case into another folder without opening the editor (§11). */
export const MoveCaseDialog = ({ projectCode, testCase, onClose }: MoveCaseDialogProps) => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const { data: folders } = useFolderTree(projectCode, 'case');
  const move = useMoveTestCases(projectCode);
  const [folderId, setFolderId] = useState('');

  useEffect(() => {
    setFolderId(testCase?.folderId ?? '');
  }, [testCase]);

  const submit = async () => {
    if (!testCase || folderId === '') return;
    try {
      await move.mutateAsync({ caseIds: [testCase.id], folderId });
      toast.success(t('cases.moved', { key: testCase.caseKey }));
      onClose();
    } catch (error) {
      toast.error(errorText(error, 'cases.moveFailed'));
    }
  };

  return (
    <Dialog
      open={testCase !== null}
      onOpenChange={(open) => !open && onClose()}
      title={t('cases.moveTitle', { key: testCase?.caseKey ?? '' })}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={move.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={move.isPending}
            disabled={folderId === ''}
          >
            {t('cases.move')}
          </Button>
        </>
      }
    >
      <Field label={t('common.folder')}>
        {({ id }) => (
          <SelectMenu
            id={id}
            value={folderId}
            onValueChange={setFolderId}
            placeholder={t('cases.folderPlaceholder')}
          >
            {flattenFolders(folders ?? []).map((option) => (
              <SelectMenuItem key={option.id} value={option.id}>
                {option.label}
              </SelectMenuItem>
            ))}
          </SelectMenu>
        )}
      </Field>
    </Dialog>
  );
};
